import { TicketsParsedDataItem } from '../../tickets/TicketsInterfaces';
import {
  BaseMetricData,
  BusinessReportingTicketAggregatedData,
  BusinessReportingMonthTotalsData,
  BusinessReportingApplicationTotalsData
} from '../BusinessReportingInterfaces';
import { aggregateMetricsData } from 'src/utilities/CommonUtilities';
import {
  BUSINESS_REPORTING_MONTH_TOTALS_COLUMN_LIST,
  BUSINESS_REPORTING_TICKET_MEASURE_COLUMN_LIST,
  BUSINESS_REPORTING_APPLICATION_TOTALS_COLUMN_LIST,
  TICKET_STATUS
} from '../BusinessReportingConstants';

export const formatTimeValue = (value: number): number => parseFloat(value.toFixed(2));

// Helper functions (extracted)
const createAggregationKey = (ticket: TicketsParsedDataItem): string => `${ticket.create_date_UserTimeZone_formatted}-${ticket.application_name}`;

const isSev2Ticket = (ticket: TicketsParsedDataItem): boolean => {
  const impactValue = ticket.impact ? parseInt(ticket.impact, 10) : NaN;
  return !isNaN(impactValue) && impactValue === 2;
};

const updateResolutionTypeCount = (map: Map<string, Record<string, number>>, key: string, resolutionType: string): void => {
  const counts = map.get(key) ?? {};
  counts[resolutionType] = (counts[resolutionType] ?? 0) + 1;
  map.set(key, counts);
};

/**
 * Aggregates resolution type counts per month per application
 * @param filteredTicketData - Filtered ticket data
 * @returns Resolution type counts aggregated by month and application
 */
export const aggregateResolutionTypeCounts = (filteredTicketData: TicketsParsedDataItem[]) => {
  const resolvedTicketsWithResolutionType = filteredTicketData.filter(
    (ticket) => ticket.status === TICKET_STATUS.RESOLVED && ticket.resolution_type?.trim()
  );

  const resolutionTypeMap = new Map<string, Record<string, number>>();
  const sev2ResolutionTypeMap = new Map<string, Record<string, number>>();

  // Early return with proper typing
  if (resolvedTicketsWithResolutionType.length === 0) {
    return { resolutionTypeMap, sev2ResolutionTypeMap };
  }

  resolvedTicketsWithResolutionType.forEach((ticket) => {
    const key = createAggregationKey(ticket);

    updateResolutionTypeCount(resolutionTypeMap, key, ticket.resolution_type);

    if (isSev2Ticket(ticket)) {
      updateResolutionTypeCount(sev2ResolutionTypeMap, key, ticket.resolution_type);
    }
  });

  return { resolutionTypeMap, sev2ResolutionTypeMap };
};

/**
 * Efficiently gets the top N items from a collection
 * @param items - Map or array of [key, value] tuples to process
 * @param valueExtractor - Function to extract numeric value from each item
 * @param n - Number of top items to return (default: 5)
 * @returns Array of [key, value] tuples sorted by value in descending order
 */
export const getTopNItems = <T>(items: Map<string, T> | [string, T][], valueExtractor: (item: T) => number, n: number = 5): [string, T][] => {
  // Convert Map to array if needed
  const itemsArray = items instanceof Map ? Array.from(items.entries()) : items;
  // Use the optimized algorithm that maintains only top N elements
  let topN: [string, T][] = [];
  for (const [key, value] of itemsArray) {
    const numericValue = valueExtractor(value);
    // Fill array until we have N elements
    if (topN.length < n) {
      topN.push([key, value]);
      if (topN.length === n) {
        // Sort ascending so smallest is at index 0
        topN.sort((a, b) => valueExtractor(a[1]) - valueExtractor(b[1]));
      }
    } else if (numericValue > valueExtractor(topN[0][1])) {
      // Replace smallest element if we find a larger one
      topN[0] = [key, value];
      // Re-sort to maintain smallest at index 0
      topN.sort((a, b) => valueExtractor(a[1]) - valueExtractor(b[1]));
    }
  }
  // Final sort to get descending order
  return topN.sort((a, b) => valueExtractor(b[1]) - valueExtractor(a[1]));
};

/**
 * Prepares ticket data for aggregation by adding computed fields
 * @param filteredTicketData - Array of filtered ticket data items
 * @returns Array of ticket data with additional aggregation fields
 */
export const prepareTicketDataForAggregation = (
  filteredTicketData: TicketsParsedDataItem[]
): (TicketsParsedDataItem & {
  total_tickets: number;
  resolved_tickets: number;
  sev2_tickets: number;
  sev3_tickets: number;
  sev4_tickets: number;
  sev5_tickets: number;
  sev2_time_spent: number;
  total_time_spent: number;
})[] => {
  return filteredTicketData.map((ticket: TicketsParsedDataItem) => ({
    ...ticket,
    total_tickets: 1,
    resolved_tickets: ticket.status === TICKET_STATUS.RESOLVED ? 1 : 0,
    sev2_tickets: parseInt(ticket.impact) === 2 ? 1 : 0,
    sev3_tickets: parseInt(ticket.impact) === 3 ? 1 : 0,
    sev4_tickets: parseInt(ticket.impact) === 4 ? 1 : 0,
    sev5_tickets: parseInt(ticket.impact) === 5 ? 1 : 0,
    sev2_time_spent: parseInt(ticket.impact) === 2 && ticket.status === TICKET_STATUS.RESOLVED ? ticket.total_time_spent : 0,
    total_time_spent: ticket.status === TICKET_STATUS.RESOLVED ? ticket.total_time_spent : 0
  }));
};

/**
 * Maps base metric data to final aggregated business reporting data
 * @param baseMetrics - Base metrics from aggregateMetricsData
 * @param resolutionTypeMap - Map of resolution types by month-application key
 * @param sev2ResolutionTypeMap - Map of SEV2 resolution types by month-application key
 * @returns Array of BusinessReportingTicketAggregatedData
 */
export const mapBaseMetricsToAggregatedData = (
  baseMetrics: BaseMetricData[],
  resolutionTypeMap: Map<string, { [key: string]: number }>,
  sev2ResolutionTypeMap: Map<string, { [key: string]: number }>
): BusinessReportingTicketAggregatedData[] => {
  return baseMetrics.map((metric: BaseMetricData) => {
    const key = `${metric.create_date_UserTimeZone_formatted}-${metric.application_name}`;
    const resolutionTypes = resolutionTypeMap.get(key) || {};
    const sev2ResolutionTypes = sev2ResolutionTypeMap.get(key);

    const result: BusinessReportingTicketAggregatedData = {
      create_date_UserTimeZone_formatted: metric.create_date_UserTimeZone_formatted,
      application_name: metric.application_name,
      total_tickets: metric.total_tickets,
      resolved_tickets: metric.resolved_tickets,
      sev2_tickets: metric.sev2_tickets,
      sev3_tickets: metric.sev3_tickets,
      sev4_tickets: metric.sev4_tickets,
      sev5_tickets: metric.sev5_tickets,
      total_time_spent: formatTimeValue(metric.total_time_spent),
      resolution_types: resolutionTypes
    };

    // Add sev2_time_spent if SEV2 tickets exist (avoid zero/empty values)
    if (metric.sev2_time_spent && metric.sev2_time_spent > 0) {
      result.sev2_time_spent = formatTimeValue(metric.sev2_time_spent);
    }

    // Only add sev2_resolution_types if SEV2 tickets exist (avoid empty mappings)
    if (sev2ResolutionTypes && Object.keys(sev2ResolutionTypes).length > 0) {
      result.sev2_resolution_types = sev2ResolutionTypes;
    }

    return result;
  });
};

/**
 * Gets unique months and applications from aggregated data
 * @param finalAggregatedData - Aggregated business reporting data
 * @returns Object with unique months and applications sets
 */
export const getUniqueMonthsAndApplications = (
  finalAggregatedData: BusinessReportingTicketAggregatedData[]
): { uniqueMonths: Set<string>; uniqueApplications: Set<string> } => {
  const uniqueMonths = new Set(finalAggregatedData.map((item) => item.create_date_UserTimeZone_formatted));
  const uniqueApplications = new Set(finalAggregatedData.map((item) => item.application_name));

  return { uniqueMonths, uniqueApplications };
};

/**
 * Calculates month totals for business reporting data
 * Note: Totals are only calculated when there are multiple months
 * @param finalAggregatedData - Aggregated business reporting data
 * @returns Array of month totals data (empty if only one month exists)
 */
export const calculateMonthTotals = (finalAggregatedData: BusinessReportingTicketAggregatedData[]): BusinessReportingMonthTotalsData[] => {
  const { uniqueMonths } = getUniqueMonthsAndApplications(finalAggregatedData);

  // Calculate month totals only if we have more than 1 month
  let monthTotals: BusinessReportingMonthTotalsData[] = [];
  if (uniqueMonths.size > 1) {
    const monthTotalsRaw = aggregateMetricsData(
      BUSINESS_REPORTING_MONTH_TOTALS_COLUMN_LIST,
      finalAggregatedData,
      BUSINESS_REPORTING_TICKET_MEASURE_COLUMN_LIST
    );

    monthTotals = monthTotalsRaw.map((monthData: BaseMetricData & { sev2_time_spent?: number }) => {
      const result: BusinessReportingMonthTotalsData = {
        create_date_UserTimeZone_formatted: monthData.create_date_UserTimeZone_formatted,
        total_tickets: monthData.total_tickets,
        resolved_tickets: monthData.resolved_tickets,
        sev2_tickets: monthData.sev2_tickets,
        sev3_tickets: monthData.sev3_tickets,
        sev4_tickets: monthData.sev4_tickets,
        sev5_tickets: monthData.sev5_tickets,
        total_time_spent: formatTimeValue(monthData.total_time_spent)
      };

      // Only add sev2_time_spent if it exists and is greater than 0
      if (monthData.sev2_time_spent && monthData.sev2_time_spent > 0) {
        result.sev2_time_spent = formatTimeValue(monthData.sev2_time_spent);
      }

      return result;
    });
  }

  return monthTotals;
};

/**
 * Calculates application totals for business reporting data
 * Note: Totals are only calculated when there are multiple applications
 * @param finalAggregatedData - Aggregated business reporting data
 * @returns Array of application totals data (empty if only one app exists)
 */
export const calculateApplicationTotals = (
  finalAggregatedData: BusinessReportingTicketAggregatedData[]
): BusinessReportingApplicationTotalsData[] => {
  const { uniqueApplications } = getUniqueMonthsAndApplications(finalAggregatedData);

  // Calculate application totals only if we have more than 1 application
  let applicationTotals: BusinessReportingApplicationTotalsData[] = [];
  if (uniqueApplications.size > 1) {
    const applicationTotalsRaw = aggregateMetricsData(
      BUSINESS_REPORTING_APPLICATION_TOTALS_COLUMN_LIST,
      finalAggregatedData,
      BUSINESS_REPORTING_TICKET_MEASURE_COLUMN_LIST
    );

    applicationTotals = applicationTotalsRaw.map((appData: BaseMetricData & { sev2_time_spent?: number }) => {
      const result: BusinessReportingApplicationTotalsData = {
        application_name: appData.application_name,
        total_tickets: appData.total_tickets,
        resolved_tickets: appData.resolved_tickets,
        sev2_tickets: appData.sev2_tickets,
        sev3_tickets: appData.sev3_tickets,
        sev4_tickets: appData.sev4_tickets,
        sev5_tickets: appData.sev5_tickets,
        total_time_spent: formatTimeValue(appData.total_time_spent)
      };

      // Only add sev2_time_spent if it exists and is greater than 0
      if (appData.sev2_time_spent && appData.sev2_time_spent > 0) {
        result.sev2_time_spent = formatTimeValue(appData.sev2_time_spent);
      }

      return result;
    });
  }

  return applicationTotals;
};
